import { inject, Injectable } from '@angular/core'
import { MeUser, UsersService } from './users.service'
import { LoggerService } from './logger.service'
import { environment } from '../environments/environment'
import { constructNameForUser } from './utils'
import Intercom, { showArticle, update } from '@intercom/messenger-js-sdk'
import { queryKeys } from './query-keys'
import { HttpClient } from '@angular/common/http'
import { injectMutation, injectQuery } from '@ngneat/query'
import { Organization } from './organizations.service'
import { APP_NAME } from './app-title-strategy'

// The messenger reports document.title to support agents unless page_title is set, and the meeting page puts the
// meeting name in the tab title. Pinning the reported title is what stops those names from reaching Intercom.
const REPORTED_PAGE_TITLE = APP_NAME

// Query params carry free-text meeting search filters, so only the path is safe to report.
const toPathOnly = (url: string): string => url.split(/[?#]/)[0]

export function buildPageAttributes(url: string): { last_page_viewed: string; page_title: string } {
  return { last_page_viewed: toPathOnly(url), page_title: REPORTED_PAGE_TITLE }
}

@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  usersService = inject(UsersService)
  loggerService = inject(LoggerService)
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()

  private booted = false

  private isEnabled(user: MeUser | null): user is MeUser & { intercomHash: string; intercomAppId: string } {
    return (
      user !== null &&
      user.organization.resolvedSettings.isIntercomEnabled &&
      user.intercomHash !== null &&
      user.intercomAppId !== null &&
      environment.production
    )
  }

  initialize() {
    const user = this.usersService.currentUser$.value
    if (this.isEnabled(user)) {
      Intercom({
        region: 'eu',
        app_id: user.intercomAppId,
        user_id: `${user.environment}-${user.id}`,
        name: constructNameForUser(user),
        email: user.email,
        company: user.organization.name,
        page_title: REPORTED_PAGE_TITLE,
        ...this.buildAttributes(user),
      })
      this.booted = true
      this.loggerService.info('Intercom initialized with organization attributes.')
    }
  }

  // Reflects the page the user is currently viewing so support sees their context when a conversation opens.
  updatePage(url: string) {
    if (this.booted && this.isEnabled(this.usersService.currentUser$.value)) {
      update(buildPageAttributes(url))
    }
  }

  private buildAttributes(user: MeUser): Record<string, unknown> {
    const org = user.organization
    const settings = org.resolvedSettings
    return {
      'User Role': user.role,
      'Organisation Name': org.name,
      'Retention Period Days': settings.retentionPeriodInDaysForCall?.toString() ?? 'Unlimited',
      'Soft Deletion Period Days': settings.softDeletionPeriodInDays,
      'Uploads Enabled': settings.allowCallUpload,
      'Sharing Enabled': settings.isSharingEnabled,
      'Calendar Sync Enabled': settings.isCalendarSynchronizationEnabled,
      'Manual Meeting Creation Enabled': settings.allowManualCallCreation,
      'Video Download Enabled': settings.allowVideoDownload,
      'Transcript Download Enabled': settings.allowTranscriptDownload,
      'Minutes Download Enabled': settings.allowMinutesDownload,
      'Outline Download Enabled': settings.allowOutlineDownload,
      'Input Language': settings.transcriberInputLanguage,
    }
  }

  getAdmins() {
    return this.query({
      queryKey: queryKeys.intercom.admins,
      queryFn: () => this.httpClient.get<any[]>('/api/intercom/admins'),
    }).result$
  }

  startConversationMutation() {
    return this.mutation({
      mutationFn: (data: { adminId: string; message: string; userId: number }) => this.httpClient.post<void>(`api/intercom/start-conversation`, data),
    })
  }

  showArticle(articleId: string) {
    showArticle(articleId)
  }

  openHelpArticle(helpLink: string) {
    const articleId = this.extractArticleId(helpLink)
    if (articleId !== null) {
      this.showArticle(articleId)
    } else {
      window.open(helpLink, '_blank', 'noopener,noreferrer')
    }
  }

  private extractArticleId(url: string): string | null {
    const match = url.match(/articles\/(\d+)/)
    return match ? match[1] : null
  }
}
